// JiraSalesforceSearch.js
import React, { useState, useEffect, useCallback } from 'react';
import apiJiraService from '../../services/jira.apiservice';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FluxCircularProgress } from '@meltwater/flux-web-components-react';
import JiraSearch from './jiraSearch';
import SalesforceTicketTable from './SalesforceTicketTable';
import styles from './jira.module.css';
import { Paper, Box, Typography, Divider, Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';
import logger from '../../utils/logger';
const asyncRetry = require('async-retry');

const JiraSalesforceSearch = () => {
  const { salesforceId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyName = searchParams.get('company');
  
  logger.debug("Salesforce search initialized", { salesforceId, companyName });
  
  const [data, setData] = useState({});
  const [jiraerror, setJiraError] = useState(false);
  const [status, setStatus] = useState("new");
  
  useEffect(() => {
    // Set the title when the component mounts
    document.title = `Salesforce Search - ${salesforceId || 'Salesforce ID Search'}`;
    
    // Optionally, you can reset the title when the component unmounts
    return () => {
      document.title = 'GSD Support Admin';
    };
  }, [salesforceId]);

  const fetchData = useCallback(async (salesforceId) => {
    setStatus("loading");
    let retryCount = 0;
    try {
      await asyncRetry(
        async (bail) => {
          const response = await apiJiraService.searchBySalesforceId(salesforceId);

          if (response.status === 'ok') {
            logger.info('Salesforce search completed successfully', { 
              salesforceId, 
              ticketCount: response.data?.length || 0 
            });
            setStatus("ok");
            setData(response.data);
            setJiraError(false);
          } else {
            logger.warn('Salesforce search failed', { 
              salesforceId, 
              status: response.status,
              statusCode: response.statusCode 
            });
            if (response.statusCode === 429) {
              // Retry only for 429 status code
              logger.debug('Rate limit exceeded, retrying...', { 
                salesforceId, 
                attempt: retryCount + 1 
              });
              retryCount++;
              // Signal async-retry to retry
              bail(new Error('Rate limit exceeded'));
              return;
            } else {
              setStatus("error");
              setJiraError(response.data || 'Search failed');
            }
          }
        },
        {
          retries: 3,
          minTimeout: 1000,
          maxTimeout: 5000,
          factor: 2,
        }
      );
    } catch (error) {
      logger.error('Error in Salesforce search', { 
        salesforceId, 
        error: error.message,
        response: error.response?.data 
      });
      setStatus("error");
      setJiraError(error.response?.data?.data || error.message || 'Search failed');
    }
  }, []);

  useEffect(() => {
    const fetchDataEffect = async () => {
      if (salesforceId) {
        await fetchData(salesforceId);
      } else {
        setStatus("error");
        setJiraError("No Salesforce ID specified");
      }
    };

    fetchDataEffect();
  }, [fetchData, salesforceId]);

  return (
    <div className={styles.Jira} style={{ fontFamily: 'Inter, Roboto, Arial, sans-serif', background: '#f6f8fa', minHeight: '100vh', padding: '20px' }}>
      <div className={styles.divStyle}>
        
        {/* Top Bar Section */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} px={2} py={1} style={{ background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', minHeight: 56, border: '2px solid #28bcbb' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Box display="flex" gap={1}>
              <Button 
                onClick={() => navigate(-1)}
                startIcon={<ArrowBackIcon />}
                variant="contained"
                size="small"
                sx={{
                  backgroundColor: '#1d9f9f',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(29, 159, 159, 0.25)',
                  fontWeight: 600,
                  '&:hover': {
                    backgroundColor: '#188182',
                    boxShadow: '0 3px 8px rgba(29, 159, 159, 0.3)'
                  },
                  '&:active': {
                    backgroundColor: '#167172',
                    boxShadow: '0 1px 4px rgba(29, 159, 159, 0.2)'
                  }
                }}
              >
                Back
              </Button>
              <Button
                onClick={() => navigate('/jira')}
                variant="contained"
                size="small"
                sx={{
                  minWidth: '40px',
                  padding: '6px',
                  backgroundColor: '#1d9f9f',
                  color: 'white',
                  boxShadow: '0 2px 6px rgba(29, 159, 159, 0.25)',
                  '&:hover': {
                    backgroundColor: '#188182',
                    boxShadow: '0 3px 8px rgba(29, 159, 159, 0.3)'
                  },
                  '&:active': {
                    backgroundColor: '#167172',
                    boxShadow: '0 1px 4px rgba(29, 159, 159, 0.2)'
                  }
                }}
              >
                <HomeIcon fontSize="small" />
              </Button>
            </Box>
            <Typography variant="h5" style={{ fontWeight: 600, letterSpacing: 0.5, color: '#1a1a1a' }} className={styles.subheader}>
              {companyName ? `"${companyName}" Tickets` : `Salesforce ID: ${salesforceId}`}
            </Typography>
            {salesforceId && (
              <Box display="flex" gap={1}>
                <Button
                  href={`https://meltwater.lightning.force.com/lightning/r/Instance__c/${salesforceId}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: '#f8f9fa',
                    color: '#0176d3',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e1e5e9',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#f0f2f5',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  View Opportunity in Salesforce →
                </Button>
                <Button
                  href={`https://meltwater.atlassian.net/issues/?jql=project%20%3D%20MWTR%20AND%20(%22Account%20%2F%20Opportunity%20URL%22~%22${salesforceId}%22%20OR%20%22Salesforce%20Account%20ID%5BShort%20text%5D%22~%22${salesforceId}%22%20OR%20description~%22${salesforceId}%22)%20ORDER%20BY%20created%20DESC`}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: '#f8f9fa',
                    color: '#0052cc',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: '1px solid #e1e5e9',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#f0f2f5',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                    }
                  }}
                >
                  View in Jira →
                </Button>
              </Box>
            )}
          </Box>
        </Box>
        
        {/* Error State */}
        {status === "error" && (
          <Paper 
            elevation={3}
            sx={{
              background: '#fff',
              borderRadius: 3,
              p: 4,
              mx: 2,
              textAlign: 'center',
              boxShadow: '0 4px 16px rgba(0,0,0,0.07)',
              border: '2px solid #28bcbb'
            }}
          >
            <Typography variant="h4" color="error" fontWeight={600} mb={2}>
              Search Failed
            </Typography>
            <Typography variant="h6" color="text.secondary" mb={3}>
              No tickets found for Salesforce ID: {salesforceId}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              {typeof jiraerror === 'string' ? jiraerror : 'Failed to search for tickets with the provided Salesforce ID.'}
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={4}>
              Please check the Salesforce ID and try again, or contact Team Triton for assistance.
            </Typography>
            
            {/* Search Component for retry */}
            <Box sx={{ maxWidth: '600px', margin: '0 auto' }}>
              <JiraSearch />
            </Box>
          </Paper>
        )}
        
        {/* Loading State */}
        {status === "loading" && (
          <Paper elevation={0} style={{ borderRadius: 14, background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', marginLeft: '20px', marginRight: '20px', padding: 32, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
            <FluxCircularProgress indeterminate size="400">
              Searching for tickets with Salesforce ID {salesforceId}...
            </FluxCircularProgress>
          </Paper>
        )}
        
        {/* Results */}
        {status === "ok" && data && (
          <Paper elevation={3} style={{ borderRadius: 14, background: '#fff', boxShadow: '0 4px 16px rgba(0,0,0,0.07)', padding: 32, border: '2px solid #28bcbb' }}>
            <Box mb={3}>
              <Box display="flex" alignItems="center" gap={2} mb={1}>
                <Typography variant="h6" className={styles.subheader} style={{ fontWeight: 600, color: '#1a1a1a' }}>
                  Jira Tickets
                </Typography>
                <Box display="flex" alignItems="center" gap={2}>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 500 }}>
                    • Salesforce ID: {salesforceId}
                  </Typography>
                  <Typography variant="body2" style={{ color: '#666', fontWeight: 500 }}>
                    • Maximum 100 tickets shown
                  </Typography>
                </Box>
              </Box>
              <Divider sx={{ my: 1 }} />
            </Box>
            <SalesforceTicketTable transformedData={data} salesforceId={salesforceId} />
          </Paper>
        )}
      </div>
    </div>
  );
};

export default JiraSalesforceSearch;